import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InvestorProfile } from '../api/portalApi';

interface Props {
  investor: InvestorProfile;
  onLogout: () => void;
  onExplore: (panel: string) => void;
}

const WaitingRoom: React.FC<Props> = ({ investor, onLogout, onExplore }) => {
  const { t } = useTranslation('landing');
  const displayName = investor.displayName || investor.name;

  return (
    <main style={{
      minHeight: '100vh',
      background: '#0a0c14',
      color: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 20px',
      fontFamily: 'inherit',
    }}>
      <img src="/logos/nomadme_sourcing_trading_mark.png" alt="NomadMe" style={{ width: 56, height: 56, borderRadius: 12, marginBottom: 16 }} />
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>
        Welcome, {displayName}
      </h1>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 36, textAlign: 'center', maxWidth: 420 }}>
        {t('waitingRoom.subtitle')}
      </p>

      <Link
        to="/shop"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'linear-gradient(135deg, #c8a06a, #a07848)',
          color: '#0a0c14',
          fontWeight: 800,
          fontSize: '0.95rem',
          padding: '12px 28px',
          borderRadius: 10,
          textDecoration: 'none',
          marginBottom: 40,
        }}
      >
        🛍️ {t('waitingRoom.browseCta')}
      </Link>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        width: '100%',
        maxWidth: 720,
        marginBottom: 40,
      }}>
        <ActionCard
          emoji="✈️"
          title={t('waitingRoom.card1Title')}
          description={t('waitingRoom.card1Desc')}
          to="/journeys"
          explore={t('waitingRoom.explore')}
        />
        <ActionCard
          emoji="🌍"
          title={t('waitingRoom.card2Title')}
          description={t('waitingRoom.card2Desc')}
          to="/globe"
          explore={t('waitingRoom.explore')}
        />
        <ActionCard
          emoji="💼"
          title={t('waitingRoom.card3Title')}
          description={t('waitingRoom.card3Desc')}
          onClick={() => onExplore('investments')}
          explore={t('waitingRoom.explore')}
        />
      </div>

      <div style={{
        padding: '16px 24px',
        background: 'rgba(200,160,106,0.06)',
        border: '1px solid rgba(200,160,106,0.15)',
        borderRadius: 12,
        maxWidth: 480,
        marginBottom: 32,
        textAlign: 'center',
      }}>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>
          <span style={{ color: '#c8a06a', fontWeight: 600 }}>{t('waitingRoom.investingInterest')}</span>{' '}
          {t('waitingRoom.investingNote')}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link to="/" style={{ color: '#64748b', fontSize: '0.85rem', textDecoration: 'none' }}>{t('waitingRoom.backHome')}</Link>
        <span style={{ color: '#334155' }}>·</span>
        <Link to="/contact-us" style={{ color: '#64748b', fontSize: '0.85rem', textDecoration: 'none' }}>{t('waitingRoom.contactUs')}</Link>
        <span style={{ color: '#334155' }}>·</span>
        <button
          type="button"
          onClick={onLogout}
          style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}
        >
          {t('waitingRoom.signOut')}
        </button>
      </div>
    </main>
  );
};

interface ActionCardProps {
  emoji: string;
  title: string;
  description: string;
  explore: string;
  to?: string;
  onClick?: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ emoji, title, description, explore, to, onClick }) => (
  <div style={{
    background: '#111827',
    border: '1px solid rgba(200,160,106,0.15)',
    borderRadius: 14,
    padding: '24px 20px',
    transition: 'border-color 0.2s',
  }}>
    <div style={{ fontSize: '1.8rem', marginBottom: 10 }}>{emoji}</div>
    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{title}</h3>
    <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.5, marginBottom: 16 }}>{description}</p>
    {to ? (
      <Link
        to={to}
        style={{
          display: 'inline-block',
          fontSize: '0.82rem',
          fontWeight: 600,
          color: '#c8a06a',
          textDecoration: 'none',
          borderBottom: '1px solid rgba(200,160,106,0.3)',
          paddingBottom: 2,
        }}
      >
        {explore}
      </Link>
    ) : (
      <button
        type="button"
        onClick={onClick}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: '0.82rem',
          fontWeight: 600,
          color: '#c8a06a',
          cursor: 'pointer',
          borderBottom: '1px solid rgba(200,160,106,0.3)',
          paddingBottom: 2,
        }}
      >
        {explore}
      </button>
    )}
  </div>
);

export default WaitingRoom;
